import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Form, Formik } from "formik";
import { t } from "ttag";
import * as Yup from "yup";
import { Button, ChevronLeft, IconButton, TextField } from "@coolremote/shared";
import { isQaBypassCredentials } from "../../../models/devicePublicRegistrationTestBypass";
import { hasPersonalDetails, NewUserFlowState } from "./newUserFlowState";
import { isCustomerNameAvailable } from "./deviceRegistrationPublicApi";
import { QA_BYPASS_CUSTOMER_NAME } from "./qaBypassMockData";
import parentStyles from "../DeviceRegistrationPublic.module.css";
import styles from "./NewUserFlow.module.css";

const validationSchema = Yup.object({
  customerName: Yup.string().required(t`Required`)
});

interface CustomerNameFormProps {
  initialValue?: string;
  submitLabel?: string;
  /** Live, debounced availability check — not used by the existing-user branch, which creates for real on submit instead. */
  asyncValidate?: (value: string) => Promise<string | undefined>;
  onSubmit: (customerName: string) => void;
}

/**
 * Presentational customer-name field. Reused as-is by CM-619 for its
 * "+ Create new customer" option — keep this free of routing/history so it
 * can be driven from a dialog just as easily as from a full-screen step.
 */
export const CustomerNameForm: React.FC<CustomerNameFormProps> = ({
  initialValue = "",
  submitLabel = t`Next`,
  asyncValidate,
  onSubmit
}) => (
  <Formik
    initialValues={{ customerName: initialValue }}
    validationSchema={validationSchema}
    onSubmit={(values) => onSubmit(values.customerName)}
  >
    {({ values, errors, touched, setFieldValue }) => (
      <Form className={styles.form}>
        <TextField
          label={`${t`Customer Name`} *`}
          fullWidth
          value={values.customerName}
          onChange={(v: string) => setFieldValue("customerName", v)}
          error={touched.customerName ? errors.customerName : undefined}
          asyncValidate={asyncValidate}
        />
        <Button type="submit" variant="contained" className={styles.primaryButton}>
          {submitLabel}
        </Button>
      </Form>
    )}
  </Formik>
);

/** Step 2 of the new-user branch (CM-618) — route wrapper around `CustomerNameForm`. */
const CustomerNameStep: React.FC = () => {
  const history = useHistory<NewUserFlowState | undefined>();
  const deviceState = history.location.state;

  useEffect(() => {
    if (!hasPersonalDetails(deviceState)) {
      history.replace("/register-device/new-user");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasPersonalDetails(deviceState)) {
    return null;
  }

  const handleBack = () => {
    history.goBack();
  };

  const handleSubmit = (customerName: string) => {
    const nextState = { ...deviceState, customerName } as NewUserFlowState;
    // Attach the typed value to *this* history entry too, so that going back
    // from Site Details lands here with the form restored instead of blank.
    history.replace(history.location.pathname, nextState);
    history.push("/register-device/new-user/site-details", nextState);
  };

  // Informational only — the final submit always re-checks for real, so an
  // empty value or a network failure just resolves as valid rather than block.
  const validateCustomerNameAsync = async (customerName: string): Promise<string | undefined> => {
    if (!customerName.trim()) {
      return undefined;
    }
    try {
      const available = await isCustomerNameAvailable(customerName.trim());
      return available ? undefined : t`This customer name is already taken`;
    } catch {
      return undefined;
    }
  };

  return (
    <div className={parentStyles.screen}>
      <div className={parentStyles.appBar}>
        <IconButton className={parentStyles.backButton} onClick={handleBack} aria-label={t`Back`}>
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={parentStyles.appBarTitle}>{t`Customer Name`}</span>
        <span />
      </div>

      <div className={styles.stepContent}>
        <CustomerNameForm
          initialValue={
            deviceState.customerName ??
            (isQaBypassCredentials(deviceState.serial, deviceState.pin)
              ? QA_BYPASS_CUSTOMER_NAME
              : undefined)
          }
          asyncValidate={validateCustomerNameAsync}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
};

export default CustomerNameStep;
